import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import {
  PrimaryButton,
  SecondaryButton,
  Input,
  Select,
  Toggle,
  KeywordPill,
} from "../components/ui-bits";
import {
  fetchKeywords,
  createKeyword,
  updateKeyword,
  deleteKeyword,
} from "../lib/api";
import { TrashIcon, PlusIcon, TagIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["location", "service", "urgency", "general"];

export default function KeywordsPage() {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKw, setNewKw] = useState({ keyword: "", category: "service", weight: 2 });
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setKeywords(await fetchKeywords());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!newKw.keyword.trim()) return;
    try {
      const created = await createKeyword({ ...newKw, keyword: newKw.keyword.trim() });
      setKeywords((prev) => [created, ...prev]);
      setNewKw({ keyword: "", category: "service", weight: 2 });
      toast.success(`Added "${created.keyword}"`);
    } catch (e) {
      toast.error("Failed to add keyword");
    }
  };

  const onToggle = async (kw) => {
    try {
      const updated = await updateKeyword(kw.id, { enabled: !kw.enabled });
      setKeywords((prev) => prev.map((k) => (k.id === kw.id ? updated : k)));
    } catch {
      toast.error("Failed to update");
    }
  };

  const onWeight = async (kw, weight) => {
    try {
      const updated = await updateKeyword(kw.id, { weight: Number(weight) });
      setKeywords((prev) => prev.map((k) => (k.id === kw.id ? updated : k)));
    } catch {
      toast.error("Failed to update weight");
    }
  };

  const onDelete = async (kw) => {
    if (!window.confirm(`Delete keyword "${kw.keyword}"?`)) return;
    try {
      await deleteKeyword(kw.id);
      setKeywords((prev) => prev.filter((k) => k.id !== kw.id));
      toast.success(`Removed "${kw.keyword}"`);
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = keywords.filter(
    (k) =>
      !filter ||
      k.keyword.toLowerCase().includes(filter.toLowerCase()) ||
      k.category.toLowerCase().includes(filter.toLowerCase())
  );

  const byCategory = CATEGORIES.map((c) => ({
    cat: c,
    items: filtered.filter((k) => k.category === c),
  })).filter((g) => g.items.length > 0);

  return (
    <Shell
      title="Keywords"
      subtitle="Weighted triggers used by the local scoring engine. No AI calls happen here."
    >
      {/* Add form */}
      <form
        onSubmit={onAdd}
        className="border border-zinc-200 bg-white p-6 mb-8 grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
      >
        <div className="md:col-span-5">
          <div className="overline mb-1.5">Keyword / phrase</div>
          <Input
            data-testid="kw-input-text"
            value={newKw.keyword}
            onChange={(e) => setNewKw({ ...newKw, keyword: e.target.value })}
            placeholder="e.g. customs broker"
            className="w-full"
          />
        </div>
        <div className="md:col-span-3">
          <div className="overline mb-1.5">Category</div>
          <Select
            data-testid="kw-input-category"
            value={newKw.category}
            onChange={(e) => setNewKw({ ...newKw, category: e.target.value })}
            className="w-full"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </div>
        <div className="md:col-span-2">
          <div className="overline mb-1.5">Weight</div>
          <Input
            data-testid="kw-input-weight"
            type="number"
            min={1}
            max={20}
            value={newKw.weight}
            onChange={(e) => setNewKw({ ...newKw, weight: Number(e.target.value) })}
            className="w-full font-mono-num"
          />
        </div>
        <div className="md:col-span-2">
          <PrimaryButton
            type="submit"
            data-testid="kw-add-btn"
            className="w-full flex items-center justify-center gap-2"
          >
            <PlusIcon size={16} weight="bold" /> Add
          </PrimaryButton>
        </div>
      </form>

      {/* Filter */}
      <div className="flex items-center justify-between mb-4">
        <Input
          data-testid="kw-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter keywords…"
          className="w-72"
        />
        <span className="text-xs text-zinc-500">{filtered.length} keywords</span>
      </div>

      {/* List */}
      <div className="border border-zinc-200 bg-white">
        {loading && (
          <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-zinc-400">No keywords match.</div>
        )}
        {byCategory.map(({ cat, items }) => (
          <div key={cat} className="border-b border-zinc-100 last:border-b-0">
            <div className="px-6 py-3 bg-zinc-50 flex items-center gap-2 border-b border-zinc-200">
              <TagIcon size={14} />
              <span className="overline">{cat}</span>
              <span className="text-xs text-zinc-400">· {items.length}</span>
            </div>
            <div>
              {items.map((kw) => (
                <div
                  key={kw.id}
                  data-testid={`kw-row-${kw.id}`}
                  className="grid grid-cols-2 md:grid-cols-12 items-center gap-3 px-4 sm:px-6 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
                >
                  <div className="col-span-2 md:col-span-5 flex items-center gap-2">
                    <KeywordPill>{kw.keyword}</KeywordPill>
                  </div>
                  <div className="md:col-span-3">
                    <Input
                      data-testid={`kw-weight-${kw.id}`}
                      type="number"
                      min={1}
                      max={20}
                      value={kw.weight}
                      onChange={(e) => onWeight(kw, e.target.value)}
                      className="w-20 font-mono-num"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Toggle
                      checked={kw.enabled}
                      onChange={() => onToggle(kw)}
                      label={kw.enabled ? "On" : "Off"}
                      testId={`kw-toggle-${kw.id}`}
                    />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <SecondaryButton
                      data-testid={`kw-delete-${kw.id}`}
                      onClick={() => onDelete(kw)}
                      className="!px-2 !py-1.5"
                    >
                      <TrashIcon size={14} />
                    </SecondaryButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}
